import { guardNumber } from "@msuite/utils/numbers";
import { sizeNumberFields } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { createColumnHelper } from "../data-table/column-helper";
import { localized } from "../../localization";

/**
 * The size columns, built for whatever row type the table actually uses.
 *
 * They used to be a ready-made array typed against `SizeFormSchema`, which made
 * them unusable in any table whose rows are a wider type: spreading them into a
 * `ColumnDef<ArticleListItem>[]` widened the array to a union TanStack rejects,
 * and the call sites reached for `as any` instead. A factory keeps the columns
 * shared and lets the row type stay honest. The bound is `Partial` because a
 * list row carries these fields nullable where a form schema requires them, and
 * the cells already render an absent value as a dash.
 */
/** The shape the size cells actually read: the three measures and a unit label. */
type SizeColumnRow = Partial<Record<(typeof sizeNumberFields)[number], number | null>> & {
	sizeUnit?: string | null;
};

export function sizeColumns<T extends SizeColumnRow>() {
	const columnHelper = createColumnHelper<T>();

	return [
		...sizeNumberFields.map((field) =>
			columnHelper.accessor((row) => row[field], {
				id: field,
				header: localized(`calculations.${field}`),
				cell: (cell) =>
					cell.getValue()
						? guardNumber(cell.getValue()).toLocaleString("de-DE", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
								style: "unit",
								unit: (cell.row.original.sizeUnit || "centimeters").replace("meters", "meter"),
							})
						: guardString(null),
			}),
		),
	];
}
